import { z } from 'zod';
import { MODEL_TIERS } from '@inference.engine/model-registry/web';
import {
  AGENT_CATEGORIES,
  type AgentDefinitionDocument,
  type AgentDefinitionMetadata,
} from './types';

type Flow = AgentDefinitionDocument['flow'];

const listingText = z.string().max(500);

/**
 * The settings an agent node carries in its `metadata`: how the entry agent is listed, and what the
 * chat offers while any agent answers. The canvas inspector and the Copilot both write them there,
 * the Copilot without a form to hold it to the limits the API saves a definition within, so each
 * is checked against them here, as the API checks the definition.
 */
const SETTINGS = {
  tag: z.enum(AGENT_CATEGORIES),
  description: listingText,
  greeting: listingText,
  placeholderPrompt: listingText,
  capabilities: z.object({
    knowledgeBases: z.boolean().optional(),
    guidelines: z.boolean().optional(),
    modelTiers: z.array(z.enum(MODEL_TIERS)).max(MODEL_TIERS.length).optional(),
    supportedModels: z
      .array(z.object({ key: z.string().min(1), provider: z.string().min(1) }))
      .max(20)
      .optional(),
  }),
  suggestions: z.boolean(),
} as const;

const SETTINGS_KEYS = Object.keys(SETTINGS) as Array<keyof typeof SETTINGS>;

type Settings = Omit<AgentDefinitionMetadata, 'name'>;

const entryOf = (flow: Flow) =>
  flow.nodes.find((node) => node.id === flow.entryNodeId && node.kind === 'agent');

/** The valid settings in `metadata`; one that would not save is left out rather than blocking it. */
function settingsIn(
  metadata: Partial<Record<keyof typeof SETTINGS, unknown>> | undefined,
): Settings {
  const settings: Record<string, unknown> = {};
  for (const key of SETTINGS_KEYS) {
    const parsed = SETTINGS[key].safeParse(metadata?.[key]);
    if (parsed.success) settings[key] = parsed.data;
  }
  return settings as Settings;
}

/**
 * The definition's metadata as it is saved: its name, and the entry agent's settings, which the
 * agent card, the catalogue and the run all read from here.
 */
export function definitionMetadataFrom(
  flow: Flow,
  metadata: AgentDefinitionMetadata,
): AgentDefinitionMetadata {
  const entry = entryOf(flow);
  return {
    name: metadata.name,
    ...settingsIn(entry?.kind === 'agent' ? entry.metadata : undefined),
  };
}

/** A draft saved before the settings lived on the entry agent gets them copied there once. */
export function withEntrySettingsSeeded(flow: Flow, metadata: AgentDefinitionMetadata): Flow {
  const entry = entryOf(flow);
  if (!entry || entry.kind !== 'agent') return flow;
  if (Object.keys(settingsIn(entry.metadata)).length > 0) return flow;
  const seeded = settingsIn(metadata);
  if (Object.keys(seeded).length === 0) return flow;
  return {
    ...flow,
    nodes: flow.nodes.map((node) =>
      node === entry ? { ...entry, metadata: { ...entry.metadata, ...seeded } } : node,
    ),
  };
}
